import React, { useRef, useEffect, useCallback } from 'react';
import { PlaybackEngine } from '../engine/PlaybackEngine';

/**
 * Full-screen canvas for lightshow playback.
 * Creates a PlaybackEngine, initializes with script data + synced time, handles resize.
 */
export default function Canvas({
  script,
  startTime,
  syncOffset,
  getSyncOffset,
  zoneIndex,
  zoneCount,
  zoneOptions,
  rowIndex,
  cueEngine,
  onComplete,
  onError,
}) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }, []);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
    };
  }, [resize]);

  useEffect(() => {
    if (!script || !canvasRef.current) return;

    const engine = new PlaybackEngine();
    engineRef.current = engine;

    engine.setCanvas(canvasRef.current);
    // #319 Lever A: let the engine pull the freshest clock offset at show start
    // (initial `syncOffset` prop is just the arming-time snapshot).
    if (getSyncOffset) engine.setSyncOffsetProvider(getSyncOffset);
    // Pass engine telemetry (framesRendered, playbackAccuracy) up so the
    // completion handler can report it.
    engine.onComplete(() => onComplete?.(engine.getTelemetry()));
    engine.onError((err) => onError?.(err));

    // Sound cues ride the SAME synced t the script renders from, so audio and
    // light cannot drift apart (MM #116). rewind() first: the engine may be
    // re-created (resize, re-render) and the playhead must start behind t=0
    // again, or every cue would count as already passed.
    if (cueEngine) {
      cueEngine.rewind();
      engine.onFrame((t) => cueEngine.frame(t));
    }

    const result = engine.initialize(
      script.code,
      script.duration,
      startTime,
      syncOffset,
      zoneIndex ?? null,
      zoneCount || 0,
      script.paramValues || {},
      rowIndex ?? null,
      zoneOptions || []
    );

    if (result.error) {
      onError?.(new Error(result.error));
      return;
    }

    const startResult = engine.start();
    if (startResult.error) {
      onError?.(new Error(startResult.error));
    }

    return () => {
      engine.stop();
      engine.reset();
    };
  }, [script, startTime, syncOffset, zoneIndex, zoneCount, rowIndex, cueEngine, onComplete, onError]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100dvw',
        height: '100dvh',
        display: 'block',
        background: '#000',
      }}
    />
  );
}
